/*
  The family tree as lists to choose from: the libraries, the families inside a library, the sub-families inside a
  family, and the items inside a branch. It only reads the shape the server sent (`parent_id` of each node).
*/

import type { FamilyTree, Id, TreeNode } from "../../../api/types";

export function nodesOfKind(tree: FamilyTree | undefined, kind: TreeNode["kind"]): TreeNode[] {
  return (tree?.nodes ?? []).filter((node) => node.kind === kind && node.state !== "withdrawn" && node.state !== "removed");
}

/** The headings or items directly under one node. */
export function childrenOfKind(tree: FamilyTree | undefined, parentId: Id | null | undefined, kind: TreeNode["kind"]): TreeNode[] {
  if (!parentId) return [];
  return nodesOfKind(tree, kind).filter((node) => node.parent_id === parentId);
}

/** Every node below `rootId`, at any depth. A loop in the data cannot hang the walk. */
export function descendantIds(tree: FamilyTree | undefined, rootId: Id | null | undefined): Set<Id> {
  const out = new Set<Id>();
  if (!rootId) return out;
  const byParent = new Map<Id, Id[]>();
  for (const node of tree?.nodes ?? []) {
    if (!node.parent_id) continue;
    const list = byParent.get(node.parent_id) ?? [];
    list.push(node.id);
    byParent.set(node.parent_id, list);
  }
  const queue: Id[] = [rootId];
  while (queue.length > 0) {
    const next = queue.shift();
    if (!next) break;
    for (const child of byParent.get(next) ?? []) {
      if (out.has(child) || child === rootId) continue;
      out.add(child);
      queue.push(child);
    }
  }
  return out;
}

/** The items that can be the main parent of a new item placed in a family or sub-family: the items of that branch. */
export function mainParentCandidates(tree: FamilyTree | undefined, headingId: Id | null | undefined): TreeNode[] {
  const inBranch = descendantIds(tree, headingId);
  return nodesOfKind(tree, "item").filter((node) => inBranch.has(node.id));
}
